import React, { useState, useEffect } from "react";
import { Box, Typography, Switch } from "@mui/material";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, type Transition } from "framer-motion";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import useLang from "../../hooks/useLang";

const STORAGE_KEY = "cookie_consent";
const ANALYTICS_KEY = "cookie_analytics";

const applyConsent = (analytics: boolean) => {
  (window as any).gtag?.("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
  });
  localStorage.setItem(STORAGE_KEY, "set");
  localStorage.setItem(ANALYTICS_KEY, analytics ? "1" : "0");
};

const bannerVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
};

const prefsVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 20 },
};

const transition: Transition = {
  duration: 0.25,
  ease: [0.16, 0.77, 0.47, 0.97],
};

const Divider: React.FC = () => (
  <Box sx={{ height: "1px", bgcolor: "rgba(255,255,255,0.07)", mx: -3 }} />
);

const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const lang = useLang();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const handleAccept = () => {
    applyConsent(true);
    setVisible(false);
  };
  const handleDecline = () => {
    applyConsent(false);
    setVisible(false);
  };
  const handleSave = () => {
    applyConsent(analytics);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 16, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 0.77, 0.47, 0.97] }}
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1400,
            width: "min(380px, calc(100vw - 32px))",
          }}
        >
          <Box
            sx={{
              bgcolor: "#1D2F41",
              boxShadow:
                "0 8px 48px rgba(0,0,0,0.28), 0 1px 0 rgba(255,255,255,0.06) inset",
              border: "1px solid rgba(255,255,255,0.07)",
              p: 3,
              overflow: "hidden",
            }}
          >
            <AnimatePresence mode="wait" initial={false}>
              {!showPrefs ? (
                /* Banner view */
                <motion.div
                  key="banner"
                  variants={bannerVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={transition}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Calibri", sans-serif',
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        letterSpacing: "0.14em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.35)",
                      }}
                    >
                      {t("cookies.title")}
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: '"Calibri", sans-serif',
                        fontSize: "0.875rem",
                        fontWeight: 300,
                        lineHeight: 1.65,
                        color: "rgba(255,255,255,0.65)",
                      }}
                    >
                      {t("cookies.body")}{" "}
                      <Link
                        to={`/${lang}/data-security`}
                        style={{
                          color: "rgba(255,255,255,0.4)",
                          textDecoration: "underline",
                          textUnderlineOffset: "3px",
                        }}
                      >
                        {t("cookies.privacyPolicy")}
                      </Link>
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Box
                        component="button"
                        onClick={handleDecline}
                        sx={{
                          flex: 1,
                          bgcolor: "transparent",
                          border: "1px solid rgba(255,255,255,0.12)",
                          color: "rgba(255,255,255,0.45)",
                          py: 1.25,
                          cursor: "pointer",
                          fontFamily: '"Calibri", sans-serif',
                          fontSize: "0.82rem",
                          fontWeight: 400,
                          letterSpacing: "0.03em",
                          transition: "border-color 0.2s, color 0.2s",
                          "&:hover": {
                            borderColor: "rgba(255,255,255,0.25)",
                            color: "rgba(255,255,255,0.7)",
                          },
                        }}
                      >
                        {t("cookies.decline")}
                      </Box>
                      <Box
                        component="button"
                        onClick={handleAccept}
                        sx={{
                          flex: 1,
                          bgcolor: "rgba(255,255,255,0.09)",
                          border: "1px solid rgba(255,255,255,0.18)",
                          color: "rgba(255,255,255,0.88)",
                          py: 1.25,
                          cursor: "pointer",
                          fontFamily: '"Calibri", sans-serif',
                          fontSize: "0.82rem",
                          fontWeight: 500,
                          letterSpacing: "0.03em",
                          transition: "background 0.2s, border-color 0.2s",
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.15)",
                            borderColor: "rgba(255,255,255,0.3)",
                          },
                        }}
                      >
                        {t("cookies.accept")}
                      </Box>
                    </Box>

                    <Box
                      component="button"
                      onClick={() => setShowPrefs(true)}
                      sx={{
                        background: "none",
                        border: "none",
                        p: 0,
                        cursor: "pointer",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Calibri", sans-serif',
                          fontSize: "0.78rem",
                          color: "rgba(255,255,255,0.3)",
                          textDecoration: "underline",
                          textUnderlineOffset: "3px",
                          "&:hover": { color: "rgba(255,255,255,0.55)" },
                          transition: "color 0.2s",
                        }}
                      >
                        {t("cookies.managePrefs")}
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              ) : (
                /* Preferences view */
                <motion.div
                  key="prefs"
                  variants={prefsVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={transition}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                  >
                    {/* Header */}
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        component="button"
                        onClick={() => setShowPrefs(false)}
                        sx={{
                          background: "none",
                          border: "none",
                          p: 0,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          color: "rgba(255,255,255,0.35)",
                          "&:hover": { color: "rgba(255,255,255,0.65)" },
                          transition: "color 0.2s",
                        }}
                      >
                        <ArrowBackIcon sx={{ fontSize: "0.95rem" }} />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: '"Calibri", sans-serif',
                          fontSize: "0.6rem",
                          fontWeight: 600,
                          letterSpacing: "0.14em",
                          textTransform: "uppercase",
                          color: "rgba(255,255,255,0.35)",
                        }}
                      >
                        {t("cookies.settingsTitle")}
                      </Typography>
                    </Box>

                    <Divider />

                    {/* Necessary */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: '"Calibri", sans-serif',
                            fontSize: "0.85rem",
                            fontWeight: 500,
                            color: "rgba(255,255,255,0.75)",
                            mb: 0.5,
                          }}
                        >
                          {t("cookies.necessary")}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"Calibri", sans-serif',
                            fontSize: "0.78rem",
                            fontWeight: 300,
                            color: "rgba(255,255,255,0.4)",
                            lineHeight: 1.55,
                          }}
                        >
                          {t("cookies.necessaryDesc")}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: '"Calibri", sans-serif',
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          letterSpacing: "0.06em",
                          color: "rgba(255,255,255,0.3)",
                          flexShrink: 0,
                          mt: 0.25,
                        }}
                      >
                        {t("cookies.alwaysOn")}
                      </Typography>
                    </Box>

                    <Divider />

                    {/* Analytics */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 2,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: '"Calibri", sans-serif',
                            fontSize: "0.85rem",
                            fontWeight: 500,
                            color: "rgba(255,255,255,0.75)",
                            mb: 0.5,
                          }}
                        >
                          {t("cookies.analytics")}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"Calibri", sans-serif',
                            fontSize: "0.78rem",
                            fontWeight: 300,
                            color: "rgba(255,255,255,0.4)",
                            lineHeight: 1.55,
                          }}
                        >
                          {t("cookies.analyticsDesc")}
                        </Typography>
                      </Box>
                      <Switch
                        checked={analytics}
                        onChange={(e) => setAnalytics(e.target.checked)}
                        size="small"
                        sx={{
                          flexShrink: 0,
                          mt: 0.25,
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#fff",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            { bgcolor: "rgba(255,255,255,0.4)" },
                          "& .MuiSwitch-track": {
                            bgcolor: "rgba(255,255,255,0.15)",
                          },
                          "& .MuiSwitch-thumb": { boxShadow: "none" },
                        }}
                      />
                    </Box>

                    <Divider />

                    {/* Save */}
                    <Box
                      component="button"
                      onClick={handleSave}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.09)",
                        border: "1px solid rgba(255,255,255,0.18)",
                        color: "rgba(255,255,255,0.88)",
                        py: 1.25,
                        cursor: "pointer",
                        fontFamily: '"Calibri", sans-serif',
                        fontSize: "0.82rem",
                        fontWeight: 500,
                        letterSpacing: "0.03em",
                        transition: "background 0.2s, border-color 0.2s",
                        "&:hover": {
                          bgcolor: "rgba(255,255,255,0.15)",
                          borderColor: "rgba(255,255,255,0.3)",
                        },
                      }}
                    >
                      {t("cookies.save")}
                    </Box>
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
